
import { format, subDays } from "date-fns";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  LineChart as LineChartIcon,
  BarChart2,
  PieChart as PieChartIcon,
  MessageSquare,
  Zap,
  Link as LinkIcon,
  User,
  ArrowUpRight,
  Download,
  Calendar,
  Filter,
  RefreshCw
} from "lucide-react";
import { useChatbotQueries } from "@/hooks/useChatbotQueries";
import { useTriggerLogs } from "@/hooks/queries/useTriggerLogs";
import { useParams } from "react-router-dom";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { useState } from "react";
import { ConversationDetailModal } from "./ConversationDetailModal";
import { ConversationsListPage } from "./ConversationsListPage";

type ConversationMetadata = {
  utm_source?: string;
  completed_at?: string;
  response_time?: number;
  user_id?: string;
  user_name?: string;
  completed?: boolean;
}

type TimePeriod = "7days" | "30days" | "90days" | "all";

export const BuilderDashboard = () => {
  const { id } = useParams();
  const { chatbot, conversations, isLoading } = useChatbotQueries(id);
  const { summary: triggerSummary } = useTriggerLogs(id);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("7days");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);
  const [showConversationsList, setShowConversationsList] = useState(false);

  // Helper function to capitalize the first letter of each word
  const capitalizeString = (str: string): string => {
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const filterConversationsByTime = () => {
    if (!conversations) return [];

    const now = new Date();
    let cutoffDate;

    switch(timePeriod) {
      case "7days":
        cutoffDate = subDays(now, 7);
        break;
      case "30days":
        cutoffDate = subDays(now, 30);
        break;
      case "90days":
        cutoffDate = subDays(now, 90);
        break;
      case "all":
      default:
        return conversations;
    }

    return conversations.filter(conv => new Date(conv.created_at) >= cutoffDate);
  };

  const filteredConversations = filterConversationsByTime();

  const prepareTimeSeriesData = () => {
    const daysToShow = timePeriod === "7days" ? 7 :
                       timePeriod === "30days" ? 30 :
                       timePeriod === "90days" ? 90 : 30;

    const timeSeriesData = Array.from({ length: daysToShow }, (_, i) => {
      const date = subDays(new Date(), i);
      return {
        date: date,
        day: format(date, "EEE"),
        fullDate: format(date, "MMM dd"),
        conversations: 0,
        completions: 0
      };
    }).reverse();

    filteredConversations.forEach(conv => {
      const convDate = new Date(conv.created_at);
      const dayData = timeSeriesData.find(day =>
        format(day.date, "yyyy-MM-dd") === format(convDate, "yyyy-MM-dd")
      );

      if (dayData) {
        dayData.conversations++;

        const metadata = conv.metadata as ConversationMetadata;
        if (metadata?.completed_at || metadata?.completed) {
          dayData.completions++;
        }
      }
    });

    return timeSeriesData;
  };

  const timeSeriesData = prepareTimeSeriesData();

  const prepareUtmData = () => {
    const utmSources = new Map();
    filteredConversations.forEach(conv => {
      const metadata = conv.metadata as ConversationMetadata;
      // Capitalize source names (e.g., "direct" -> "Direct")
      const source = capitalizeString(metadata?.utm_source || "direct");
      utmSources.set(source, (utmSources.get(source) || 0) + 1);
    });

    return Array.from(utmSources.entries())
      .map(([source, visits]) => ({ source, visits }))
      .sort((a, b) => (b.visits as number) - (a.visits as number));
  };

  const utmData = prepareUtmData();

  const conversionRateData = timeSeriesData.map(day => ({
    day: day.fullDate,
    rate: day.conversations > 0
      ? Math.round((day.completions / day.conversations) * 100)
      : 0
  }));

  const totalConversations = filteredConversations.length || 0;
  const completedConversations = filteredConversations.filter(conv => {
    const metadata = conv.metadata as ConversationMetadata;
    return !!metadata?.completed_at || !!metadata?.completed;
  }).length || 0;

  const conversionRate = totalConversations > 0
    ? Math.round((completedConversations / totalConversations) * 100)
    : 0;

  const totalTriggersFired = triggerSummary?.totalExecutions || 0;

  const recentConversations = [...filteredConversations]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28DFF'];

  const handleTimePeriodChange = (period: TimePeriod) => {
    setIsRefreshing(true);
    setTimePeriod(period);

    // Simulate a small delay for a refresh effect
    setTimeout(() => {
      setIsRefreshing(false);
    }, 300);
  };

  const exportData = () => {
    // Create CSV content
    const csvContent = [
      // CSV Headers
      ["Date", "Conversations", "Completions", "Conversion Rate"],
      // CSV Data rows
      ...timeSeriesData.map(d => [
        d.fullDate,
        d.conversations,
        d.completions,
        d.conversations > 0 ? Math.round((d.completions / d.conversations) * 100) + "%" : "0%"
      ])
    ]
    .map(row => row.join(","))
    .join("\n");

    // Create a blob and download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    // Setup and trigger download
    link.setAttribute("href", url);
    link.setAttribute("download", `chatbot-data-${timePeriod}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConversationClick = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setIsConversationModalOpen(true);
  };

  const handleCloseConversationModal = () => {
    setIsConversationModalOpen(false);
    setSelectedConversationId(null);
  };

  const handleViewAllConversations = () => {
    setShowConversationsList(true);
  };

  const handleBackToDashboard = () => {
    setShowConversationsList(false);
  };

  // Calculate comparison metrics for previous period
  const calculateComparisons = () => {
    if (!conversations) return {
      conversationsChange: { value: "N/A", positive: true },
      triggersChange: { value: "N/A", positive: true },
      conversionRateChange: { value: "N/A", positive: true },
      completionsChange: { value: "N/A", positive: true }
    };

    const now = new Date();
    let currentPeriodStart: Date;
    let previousPeriodStart: Date;
    let previousPeriodEnd: Date;

    // Define current and previous period boundaries
    switch(timePeriod) {
      case "7days":
        currentPeriodStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        previousPeriodStart = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
        previousPeriodEnd = currentPeriodStart;
        break;
      case "30days":
        currentPeriodStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        previousPeriodStart = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
        previousPeriodEnd = currentPeriodStart;
        break;
      case "90days":
        currentPeriodStart = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        previousPeriodStart = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
        previousPeriodEnd = currentPeriodStart;
        break;
      default: // "all"
        // For "all time", compare with previous year
        currentPeriodStart = new Date(now.getFullYear(), 0, 1); // Start of current year
        previousPeriodStart = new Date(now.getFullYear() - 1, 0, 1); // Start of previous year
        previousPeriodEnd = new Date(now.getFullYear() - 1, 11, 31); // End of previous year
        break;
    }

    // Filter conversations for current period
    const currentPeriodConversations = conversations.filter(conv => {
      const convDate = new Date(conv.created_at);
      return convDate >= currentPeriodStart && convDate <= now;
    });

    // Filter conversations for previous period
    const previousPeriodConversations = conversations.filter(conv => {
      const convDate = new Date(conv.created_at);
      return convDate >= previousPeriodStart && convDate <= previousPeriodEnd;
    });

    // Calculate metrics for both periods
    const currentMetrics = {
      total: currentPeriodConversations.length,
      completed: currentPeriodConversations.filter(conv => {
        const metadata = conv.metadata as ConversationMetadata;
        return !!metadata?.completed_at || !!metadata?.completed;
      }).length
    };

    const previousMetrics = {
      total: previousPeriodConversations.length,
      completed: previousPeriodConversations.filter(conv => {
        const metadata = conv.metadata as ConversationMetadata;
        return !!metadata?.completed_at || !!metadata?.completed;
      }).length
    };

    // Calculate percentage changes
    const calculateChange = (current: number, previous: number) => {
      if (previous === 0) return current > 0 ? { value: "New!", positive: true } : { value: "0%", positive: true };
      const change = ((current - previous) / previous) * 100;
      return {
        value: `${change >= 0 ? '+' : ''}${Math.round(change)}%`,
        positive: change >= 0
      };
    };

    const currentConversionRate = currentMetrics.total > 0 ? (currentMetrics.completed / currentMetrics.total) * 100 : 0;
    const previousConversionRate = previousMetrics.total > 0 ? (previousMetrics.completed / previousMetrics.total) * 100 : 0;

    return {
      conversationsChange: calculateChange(currentMetrics.total, previousMetrics.total),
      triggersChange: {
        value: totalTriggersFired > 0 ? `${totalTriggersFired} total` : "No triggers",
        positive: totalTriggersFired > 0
      },
      conversionRateChange: calculateChange(currentConversionRate, previousConversionRate),
      completionsChange: calculateChange(currentMetrics.completed, previousMetrics.completed)
    };
  };

  const comparisons = calculateComparisons();

  if (isLoading || isRefreshing) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-primary/70" />
          <span className="ml-2 text-lg text-muted-foreground">Loading dashboard data...</span>
        </div>
      </div>
    );
  }

  // Show conversations list page if requested
  if (showConversationsList) {
    return (
      <div className="p-6">
        <ConversationsListPage
          chatbotId={id!}
          onBack={handleBackToDashboard}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {chatbot && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl font-bold">{chatbot.name}</CardTitle>
                {chatbot.description && (
                  <CardDescription className="mt-1">
                    {chatbot.description}
                  </CardDescription>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className={`px-2 py-1 text-xs rounded-full ${
                  chatbot.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                }`}>
                  {chatbot.active ? "Active" : "Inactive"}
                </div>
                <div className="text-xs text-muted-foreground">
                  Created {new Date(chatbot.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Dashboard Overview</h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            <Button
              variant={timePeriod === "7days" ? "default" : "outline"}
              size="sm"
              onClick={() => handleTimePeriodChange("7days")}
              className="rounded-r-none border-r-0"
            >
              7 Days
            </Button>
            <Button
              variant={timePeriod === "30days" ? "default" : "outline"}
              size="sm"
              onClick={() => handleTimePeriodChange("30days")}
              className="rounded-none border-r-0"
            >
              30 Days
            </Button>
            <Button
              variant={timePeriod === "90days" ? "default" : "outline"}
              size="sm"
              onClick={() => handleTimePeriodChange("90days")}
              className="rounded-none border-r-0"
            >
              90 Days
            </Button>
            <Button
              variant={timePeriod === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => handleTimePeriodChange("all")}
              className="rounded-l-none"
            >
              All Time
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={exportData}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Conversations"
          value={totalConversations.toString()}
          change={{
            value: comparisons.conversationsChange.value,
            label: "from previous period",
            positive: comparisons.conversationsChange.positive
          }}
          icon={MessageSquare}
        />
        <StatCard
          title="Triggers Fired"
          value={totalTriggersFired.toString()}
          change={{
            value: comparisons.triggersChange.value,
            label: "executions",
            positive: comparisons.triggersChange.positive
          }}
          icon={Zap}
        />
        <StatCard
          title="Conversion Rate"
          value={`${conversionRate}%`}
          change={{
            value: comparisons.conversionRateChange.value,
            label: comparisons.conversionRateChange.positive ? "increase" : "decrease",
            positive: comparisons.conversionRateChange.positive
          }}
          icon={LinkIcon}
        />
        <StatCard
          title="Completed Conversations"
          value={completedConversations.toString()}
          change={{
            value: comparisons.completionsChange.value,
            label: "from previous period",
            positive: comparisons.completionsChange.positive
          }}
          icon={User}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg font-semibold">Conversation Overview</CardTitle>
              <Button variant="outline" size="sm">
                <LineChartIcon className="h-4 w-4 mr-2" />
                Details
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={timeSeriesData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorConversations" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorCompletions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey={timePeriod === "7days" ? "day" : "fullDate"}
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    height={20}
                  />
                  <YAxis
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    width={30}
                  />
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
                  <Tooltip
                    formatter={(value, name) => {
                      // Fix the tooltip labels here
                      if (name === "conversations") {
                        return [value, "Conversations"];
                      } else if (name === "completions") {
                        return [value, "Completions"];
                      }
                      return [value, name];
                    }}
                    labelFormatter={(label) => `Date: ${label}`}
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="conversations"
                    stroke="hsl(var(--primary))"
                    fillOpacity={1}
                    fill="url(#colorConversations)"
                    strokeWidth={2}
                    name="conversations"
                  />
                  <Area
                    type="monotone"
                    dataKey="completions"
                    stroke="#22c55e"
                    fillOpacity={1}
                    fill="url(#colorCompletions)"
                    strokeWidth={2}
                    name="completions"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center mt-2 space-x-6">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-primary mr-2"></div>
                <span className="text-xs">Conversations</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-xs">Completions</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-0">
            <div className="flex justify-between items-start">
              <CardTitle className="text-lg font-semibold">Traffic Sources</CardTitle>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[220px]">
              <div className="flex items-center justify-center h-full">
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={utmData.slice(0, 5)}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="visits"
                      label={({ source }) => source}
                    >
                      {utmData.slice(0, 5).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [`${value} visits`, props.payload.source]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col justify-center h-full">
                <h4 className="text-sm font-medium mb-3">Top Sources</h4>
                <div className="space-y-2">
                  {utmData.slice(0, 5).map((item, index) => (
                    <div key={item.source} className="flex justify-between items-center">
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm">{item.source}</span>
                      </div>
                      <div className="text-sm font-medium">{item.visits}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-0">
            <CardTitle className="text-lg font-semibold">Recent Conversations</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Response Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentConversations.map((conv) => {
                  const metadata = conv.metadata as ConversationMetadata;
                  const completed = !!metadata?.completed_at || !!metadata?.completed;

                  return (
                    <TableRow
                      key={conv.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleConversationClick(conv.id)}
                    >
                      <TableCell>
                        {format(new Date(conv.created_at), "MMM dd, yyyy HH:mm")}
                      </TableCell>
                      <TableCell>{capitalizeString(metadata?.utm_source || "direct")}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          completed
                            ? "bg-green-100 text-green-800"
                            : "bg-amber-100 text-amber-800"
                        }`}>
                          {completed ? "Completed" : "In Progress"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {metadata?.response_time
                          ? `${metadata.response_time.toFixed(1)}s`
                          : "N/A"}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {recentConversations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">
                      No recent conversations found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
          <CardFooter className="flex justify-end pt-0">
            <Button variant="outline" size="sm" onClick={handleViewAllConversations}>
              View All Conversations
            </Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-lg font-semibold">Conversion Rate</CardTitle>
          </CardHeader>
          <CardContent className="pt-3">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={conversionRateData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
                  <XAxis
                    dataKey="day"
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tick={{
                      textAnchor: 'end',
                      dominantBaseline: 'auto',
                      dy: 6
                    }}
                    height={30}
                  />
                  <YAxis
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    formatter={(value) => [`${value}%`, "Conversion Rate"]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="rate"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversation Detail Modal */}
      <ConversationDetailModal
        conversationId={selectedConversationId}
        isOpen={isConversationModalOpen}
        onClose={handleCloseConversationModal}
      />
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: string;
  change: {
    value: string;
    label: string;
    positive: boolean;
  };
  icon: React.ComponentType<{ className?: string }>;
}

const StatCard = ({ title, value, change, icon: Icon }: StatCardProps) => {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <h3 className="text-2xl font-bold mt-2">{value}</h3>
          </div>
          <div className="p-2 bg-primary/10 rounded-full">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
        <div className="flex items-center mt-4 text-sm">
          <ArrowUpRight className={`h-4 w-4 mr-1 ${change.positive ? 'text-green-500' : 'text-red-500'}`} />
          <span className={change.positive ? 'text-green-500' : 'text-red-500'}>
            {change.value}
          </span>
          <span className="text-muted-foreground ml-2">{change.label}</span>
        </div>
      </CardContent>
    </Card>
  );
};
